/**
 * Pixel injector — dynamically loads pixel SDKs based on consent.
 */

import type { ConsentMap, ModuleConfig, PixelConfig } from '../types/consent.d';
import { fireEvent } from './event-dispatcher';

const injected: Record<string, boolean> = {};

export function applyConsent(config: ModuleConfig, consent: ConsentMap): void {
  if (!config.pixels || config.pixels.length === 0) return;

  for (const pixel of config.pixels) {
    const key = pixel.type + '_' + (pixel.pixel_id || 'custom');
    if (injected[key]) continue;
    if (!isGranted(pixel.category, consent)) continue;

    injectSnippet(pixel);
    injected[key] = true;
  }

  // Allow pixel SDKs a moment to initialise before firing events
  setTimeout(() => flushEventQueue(config, consent), 500);
}

function isGranted(category: string, consent: ConsentMap): boolean {
  if (category === 'necessary') return true;
  return !!consent[category as keyof ConsentMap];
}

function injectSnippet(pixel: PixelConfig): void {
  if (!pixel.snippet) return;

  const target = pixel.position === 'head' ? document.head : document.body;
  const container = document.createElement('div');
  container.innerHTML = pixel.snippet;

  // Extract and re-create <script> elements so the browser executes them
  const scripts = container.querySelectorAll('script');
  for (const oldScript of scripts) {
    const newScript = document.createElement('script');

    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }

    if (oldScript.textContent) {
      newScript.textContent = oldScript.textContent;
    }

    target.appendChild(newScript);
  }

  // Append non-script nodes (noscript, img tracking pixels, etc.)
  let node = container.firstChild;
  while (node) {
    const next = node.nextSibling;
    if (node.nodeName !== 'SCRIPT') {
      target.appendChild(node);
    }
    node = next;
  }
}

function flushEventQueue(config: ModuleConfig, consent: ConsentMap): void {
  if (!config.eventQueue || config.eventQueue.length === 0) return;

  const queue = config.eventQueue;
  config.eventQueue = [];

  for (const entry of queue) {
    if (!entry.event) continue;
    fireEvent(entry.event, entry.data || {}, config, consent, injected);
  }
}
